/**
 * TimeSlotSelector - Main component for selecting available time slots
 * Displays filterable time slots organized by time of day (Morning, Afternoon, Evening)
 * 
 * @example
 * ```tsx
 * <TimeSlotSelector
 *   slots={availabilityData}
 *   bays={baysList}
 *   durationOptions={[30, 60, 90, 120]}
 *   selectedBayId={1}
 *   selectedDuration={60}
 *   selectedSlots={selectedSlot !== null ? [selectedSlot] : []}
 *   isMember={false}
 *   onBayChange={(bayId) => setBayId(bayId)}
 *   onDurationChange={(duration) => setDuration(duration)}
 *   onSlotsSelect={(slots) => handleBooking(slots)}
 * />
 * ```
 */

import { useState } from 'react';
import { isStartTooSoon } from '@butter/library/availability';
import { BottomSheet } from '@butter/ui/patterns';
import { EmptyState } from '@butter/ui/components/feedback';
import { FilterIcon, HourglassIcon, ChevronIcon, ClockIcon } from '@butter/ui/icons';
import { useNow } from '~/hooks/useNow';
import { TimeSlotFilters } from './TimeSlotFilters';
import { TimeSlotSegment } from './TimeSlotSegment';
import { TimeSlotChip } from './TimeSlotChip';
import { DurationSheet } from './DurationSheet';
import { TimeSlotGridSkeleton } from './TimeSlotGridSkeleton';
import { TIME_SEGMENTS, canFitDuration, formatDuration, getConsecutiveSlots, getSelectionAnnouncement } from './utils';
import type { TimeSlot, TimeSlotSelectorProps, TimeSlotState } from './types';

/** How often an open grid re-reads the clock to drop starts that have become too soon. */
const CLOCK_TICK_MS = 30_000;

const EMPTY_TITLE = 'No available time slots';
const EMPTY_DESCRIPTION = 'Try another day or adjust your filters.';

export function TimeSlotSelector({
  title = 'Available time slots',
  durationDisplay = 'select',
  slots,
  bays,
  durationOptions,
  selectedBayId,
  selectedDuration,
  selectedSlots,
  selectedRange,
  isMember = false,
  onBayChange,
  onDurationChange,
  onSlotsSelect,
  isLoading = false,
  error = null,
  graceMinutes = 0,
  noticeMinutes = 0,
  availableDurations,
  hideFilters = false,
  showBayFilter = true,
  formatOptionLabel,
  formatTriggerLabel,
}: TimeSlotSelectorProps) {
  // Mobile-only: the header funnel collapses the inline filters into a sheet.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Mobile-only: the header duration pill opens its own sheet.
  const [durationSheetOpen, setDurationSheetOpen] = useState(false);
  // Every rendered cell means the same thing: tap it to book from here. There used to be
  // one exception — the cell at the selection's END opened the length sheet instead, on
  // mobile only — and it could not be made honest. That cell is usually a perfectly valid
  // start (9:30 PM after an 8:00–9:30 booking), it looked identical to every other cell,
  // it behaved differently by viewport, and it consumed the obvious gesture for moving a
  // booking later. Length changes come from the header pill, which is visible and works
  // at any time.
  const handleSlotClick = (slot: TimeSlot) => {
    onSlotsSelect(getConsecutiveSlots(slot, slots, selectedDuration));
  };

  // Hide slots whose start time is already in the past — beyond the club's grace,
  // which keeps the 3:15 slot for a 3:16 booker, as the server does — or, on a
  // standard booking, inside the club's notice (`now + notice − grace`), which a
  // grid left open drifts into.
  //
  // Deliberately NOT converted to club time. `slot.startTime` is an ISO 8601 instant the
  // API built with `fromZonedTime`, and `Date.now()` is epoch ms — comparing two
  // absolute instants is frame-independent. The server does the same on purpose and
  // documents why (`getClubAvailabilityWithStates.ts:377`): club time decides which
  // calendar day counts as "today", but a `ZonedDate`'s shifted timestamp gives the
  // WRONG answer in a `<` / `>` comparison. Normalizing here would introduce the bug
  // that comment exists to prevent.
  //
  // Belt-and-braces, not the primary gate: the server already marks past slots
  // `isAvailable: false` when the requested date is today, and `canFitDuration` drops
  // those. This catches drift between fetch and now, which is why the clock is state
  // re-read every `CLOCK_TICK_MS`: a grid left open drops a start the moment it slips
  // past the edge, rather than when the next refetch or click happens to re-render it.
  const now = useNow(CLOCK_TICK_MS);
  const visibleSlots = slots.filter((s) => !isStartTooSoon(new Date(s.startTime), { noticeMinutes, graceMinutes }, now));

  // A cell can be on the grid for three different reasons, and only the first is
  // clickable:
  //
  //   FITTING — a bay is open for the full selected duration starting here.
  //   COVERED — part of the current booking, but could not START a fresh one of this
  //             length. Real at the tail of the day: a 90-min 8:00–9:30 booking at a
  //             club closing at 10:00 covers 8:00/8:30/9:00, and 9:00 cannot host
  //             another 90 minutes. Without this the selection outlined 2 of its 3
  //             cells and the highlight looked broken exactly where it matters.
  //
  // A cell that is NEITHER is not rendered, on either width. There was briefly a third
  // reason — the mobile extend cell, rendered so the adjacent-tap affordance survived at
  // the end of the day — and it was a mistake: `extendOnAdjacentTap` is `isMobile`, so it
  // put a cell on the mobile grid that desktop never showed, styled exactly like a
  // bookable start, that opened the length sheet instead of moving the booking. The
  // affordance is not worth a phantom slot; the header pill changes length at any time.
  // The tap still works wherever the cell at the selection's end is a valid start in its
  // own right, which is most of the day.
  const isFitting = (s: TimeSlot) => canFitDuration(s, slots, selectedDuration, isMember);
  const isCovered = (s: TimeSlot) => selectedSlots.includes(s.startMinutes);

  const renderedSlots = visibleSlots.filter((s) => isFitting(s) || isCovered(s));

  // The single source of truth for what a cell looks like AND whether it accepts a click.
  // Click-time selection stays arithmetic-only (see getConsecutiveSlots): a `covered`
  // cell is inert, and where the cell at the selection's end happens to be a valid start,
  // `handleSlotClick` intercepts it before `getConsecutiveSlots`.
  const slotState = (s: TimeSlot): TimeSlotState => {
    if (isCovered(s)) return isFitting(s) ? 'selected' : 'covered';
    // Everything else on the grid is fitting — `renderedSlots` admits nothing else.
    return 'default';
  };

  // Header + sheets outlive the loading/error/empty states: picking a duration
  // refetches the grid, and an early return would unmount the open sheet.
  const header = (
    <div className="flex flex-row items-center justify-between gap-4">
      <h3
      className="text-[20px] font-semibold text-fg-secondary"
      data-testid="booking-time-slot-header"
      >
        {title}
      </h3>

      {/* Desktop: inline filters (unchanged). */}
      {!hideFilters && (
        <div className="hidden md:block">
          <TimeSlotFilters
            durationDisplay={durationDisplay}
            bays={bays}
            selectedBayId={selectedBayId}
            durationOptions={durationOptions}
            availableDurations={availableDurations}
            selectedDuration={selectedDuration}
            onBayChange={onBayChange}
            onDurationChange={onDurationChange}
            showBayFilter={showBayFilter}
            formatOptionLabel={formatOptionLabel}
            formatTriggerLabel={formatTriggerLabel}
          />
        </div>
      )}

      {/* Mobile: duration pill + funnel button opens the filter bottom sheet */}
      <div className="flex items-center gap-2 md:hidden">
        {durationDisplay === 'select' && (
          <button
            type="button"
            onClick={() => setDurationSheetOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={durationSheetOpen}
            aria-label={`Booking length, ${formatDuration(selectedDuration)}`}
            data-testid="booking-duration-select"
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-butter-gray-300 bg-white px-3 py-2 text-sm font-semibold text-fg-secondary text-interactive:bg-bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-600 focus-visible:ring-offset-2"
          >
            <HourglassIcon className="size-[18px] shrink-0 text-fg-primary" />
            {(formatTriggerLabel ?? formatDuration)(selectedDuration)}
            <ChevronIcon className="size-3 rotate-90" />
          </button>
        )}

        {durationDisplay === 'label' && (
          <span
            data-testid="booking-duration-label"
            className="inline-flex items-center gap-2 rounded-full border border-butter-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-fg-secondary"
          >
            <HourglassIcon className="size-[18px] shrink-0 text-fg-primary" />
            {formatDuration(selectedDuration)}
          </span>
        )}

        {!hideFilters && showBayFilter && (
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="-mr-2 inline-flex size-11 items-center justify-center rounded-lg text-fg-primary transition-colors text-interactive:bg-bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-600 focus-visible:ring-offset-2"
            aria-label="Filter time slots"
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            data-testid="booking-time-slot-filter-button"
          >
            <FilterIcon className="size-6" />
          </button>
        )}
      </div>
    </div>
  );

  const content = isLoading ? (
    <TimeSlotGridSkeleton />
  ) : error ? (
    <div
      className="rounded-lg border-2 border-red-200 bg-red-50 p-6"
      role="alert"
    >
      <h3 className="mb-2 text-sm font-semibold text-red-800">
        Unable to load time slots
      </h3>
      <p className="text-sm text-red-700">{error}</p>
    </div>
  ) : renderedSlots.length === 0 ? (
    // Empty is judged on what would render, not the raw response — past and
    // member-only slots are still in `slots`.
    <EmptyState
      variant="muted"
      icon={<ClockIcon className="size-7 text-ink-muted" />}
      title={EMPTY_TITLE}
      description={EMPTY_DESCRIPTION}
      data-testid="time-slots-empty-state"
    />
  ) : (
    <>
      {/* Selected slot chip — reads the authoritative range (from the parent's
          startTime/endTime), not the filtered grid, so it still reads correctly
          even if the selection spans slots that aren't rendered as valid start
          buttons. No fallback derivation: a caller without a range has no
          selection. */}
      {selectedRange && (
        <TimeSlotChip
          startMinutes={selectedRange.startMinutes}
          endMinutes={selectedRange.endMinutes}
          onClear={() => onSlotsSelect([])}
        />
      )}

      {/* Screen reader announcement for selection changes */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-label="Time slot selection status"
        className="sr-only"
      >
        {getSelectionAnnouncement(selectedRange)}
      </div>

      {/* Time slots organized by segment */}
      <div className="space-y-8 pt-3">
        {TIME_SEGMENTS.map((segment) => (
          <TimeSlotSegment
            key={segment.id}
            segment={segment}
            slots={renderedSlots}
            slotState={slotState}
            onSlotClick={handleSlotClick}
          />
        ))}
      </div>

      {/* Member-only notice */}
      {/* {!isMember && slots.some((slot) => slot.canBookMember && !slot.canBookPublic) && (
        <div
          className="rounded-lg border-2 border-theme-600 bg-theme-50 p-4"
          role="note"
        >
          <p className="text-sm text-fg-primary">
            <span className="font-semibold text-theme-600">Members</span> have access
            to additional time slots.{' '}
            <a
              href="/membership"
              className="font-medium text-theme-600 underline hover:text-theme-700"
            >
              Learn more about membership
            </a>
          </p>
        </div>
      )} */}
    </>
  );

  return (
    <div className="space-y-6 mt-8">
      {header}
      {content}

      {/* Mobile filter sheet — desktop never mounts this (button is md:hidden) */}
      {!hideFilters && showBayFilter && (
        <BottomSheet
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          title="Filters"
          footer={
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="w-full rounded-xl bg-theme-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-theme-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-600 focus-visible:ring-offset-2"
            >
              Done
            </button>
          }
        >
          {/* `durationDisplay="hidden"` — length lives in its own pill/sheet on mobile, so
              this instance renders bays only. `durationOptions`/`selectedDuration` are
              still required by the props type; `onDurationChange` is unreachable here. */}
          <TimeSlotFilters
            layout="stacked"
            durationDisplay="hidden"
            bays={bays}
            selectedBayId={selectedBayId}
            durationOptions={durationOptions}
            selectedDuration={selectedDuration}
            onBayChange={onBayChange}
            onDurationChange={onDurationChange}
          />
        </BottomSheet>
      )}

      {/* Mobile duration sheet — opened from the header pill */}
      <DurationSheet
        open={durationSheetOpen}
        onOpenChange={setDurationSheetOpen}
        durationOptions={durationOptions}
        selectedDuration={selectedDuration}
        disabledDurations={availableDurations ? durationOptions.filter((d) => !availableDurations.includes(d)) : undefined}
        onSelect={onDurationChange}
        formatOptionLabel={formatOptionLabel}
      />
    </div>
  );
}
